// =============================================================================
// FILE: packages/renderer/src/stores/pfe.store.ts
// PURPOSE: Lightweight Zustand-free global state for the Project File
//          Explorer drawer. Two pieces only: is the drawer open + which
//          project is active. Keystroke handlers anywhere in the app can
//          flip these without prop-drilling.
// =============================================================================

import { useSyncExternalStore } from 'react';

interface PfeState { open: boolean; projectId: string | null }

const subscribers = new Set<() => void>();
let state: PfeState = {
  open: false,
  projectId: typeof window === 'undefined'
    ? null
    : localStorage.getItem('pfe.lastProjectId'),
};

function notify(): void { for (const s of subscribers) s(); }

export const pfeStore = {
  getState: (): PfeState => state,
  open(projectId?: string | null): void {
    const next = projectId ?? state.projectId ?? 'PREVIEW';
    state = { open: true, projectId: next };
    if (state.projectId) localStorage.setItem('pfe.lastProjectId', state.projectId);
    notify();
  },
  close(): void { state = { ...state, open: false }; notify(); },
  toggle(projectId?: string | null): void {
    if (state.open) pfeStore.close(); else pfeStore.open(projectId);
  },
  setProject(projectId: string | null): void {
    state = { ...state, projectId };
    if (projectId) localStorage.setItem('pfe.lastProjectId', projectId);
    else           localStorage.removeItem('pfe.lastProjectId');
    notify();
  },
  subscribe(fn: () => void): () => void {
    subscribers.add(fn);
    return () => subscribers.delete(fn);
  },
};

export function usePfeStore(): PfeState {
  return useSyncExternalStore(pfeStore.subscribe, pfeStore.getState, pfeStore.getState);
}
